'use client';

import Image from 'next/image';

interface AuthLayoutProps {
  children: React.ReactNode;
}

const featureCards = [
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    ),
    title: 'Centralized Dashboard',
    desc: 'All clients, notices & demands in one view',
  },
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M13 10V3L4 14h7v7l9-11h-7z" />
    ),
    title: 'AI-Powered Responses',
    desc: 'Generate notice replies in seconds',
  },
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    ),
    title: 'Smart Reminders',
    desc: 'Never miss a deadline again',
  },
];

const trustBadges = [
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    ),
    label: '256-bit encrypted',
  },
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    ),
    label: 'Bank-grade security',
  },
  {
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
    ),
    label: 'Data stored in India',
  },
];

export default function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen font-satoshi">

      {/* ── Left Panel ─────────────────────────────────────────── */}
      <div className="w-0 lg:w-[55%] relative overflow-hidden hidden lg:flex flex-col bg-gradient-to-br from-[#4F8EF7] via-[#5A7DE8] to-[#7B8FEB]">

        {/* Dot grid texture */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }}
        />

        {/* Gradient accents */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-violet-400/20 rounded-full blur-3xl" />
        </div>


        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-12 xl:p-16">

          {/* Agent status badge */}
          <div className="mb-auto">
            <span className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-white/15 border border-white/25 text-white">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              AI Agent · Always On
            </span>
          </div>

          {/* Headline + agentic copy */}
          <div className="flex-grow flex items-center">
            <div className="max-w-lg">
              <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-[1.15]">
                Tax compliance,{' '}
                <span className="relative inline-block text-amber-300">
                  simplified
                  <svg
                    className="absolute -bottom-1 left-0 w-full"
                    height="8"
                    viewBox="0 0 200 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M2 5.5 C40 2, 80 7, 120 4 S170 2, 198 5.5"
                      stroke="#FCD34D"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </h1>
              <p className="text-base text-white/75 mb-10 leading-relaxed">
                Your AI compliance agent monitors notices, drafts responses, and ensures you never miss a deadline — automatically.
              </p>

              {/* Feature cards */}
              <div className="space-y-2.5">
                {featureCards.map((card, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 px-4 py-3.5 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm"
                  >
                    <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                      <svg className="w-[18px] h-[18px] text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {card.icon}
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{card.title}</p>
                      <p className="text-white/60 text-xs mt-0.5">{card.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Trust strip */}
              <div
                className="mt-8 pt-6 flex items-center gap-5"
                style={{ borderTop: '1px solid rgba(255,255,255,0.18)' }}
              >
                {trustBadges.map((badge, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-white/70 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {badge.icon}
                    </svg>
                    <span className="text-xs text-white/70 whitespace-nowrap">{badge.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-auto pt-8 flex items-center justify-between">
            <div className="text-xs text-white/50">
              &copy; {new Date().getFullYear()} Finkr
            </div>
            <div className="flex items-center gap-4">
              <a href="https://www.finkr.ai/policy" target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 hover:text-white transition-colors">Privacy</a>
              <a href="https://www.finkr.ai/terms/" target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 hover:text-white transition-colors">Terms</a>
              <a href="https://www.finkr.ai/security/" target="_blank" rel="noopener noreferrer" className="text-xs text-white/50 hover:text-white transition-colors">Security</a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right Panel ────────────────────────────────────────── */}
      <div suppressHydrationWarning className="w-full lg:w-[45%] bg-white relative flex flex-col min-h-screen overflow-hidden">
        <div className="p-6 lg:p-8 relative z-10 w-full">
          <a href="https://www.finkr.ai/" target="_blank" rel="noopener noreferrer">
            <Image
              src="/finkr-logo.svg"
              alt="Finkr Logo"
              width={140}
              height={42}
              priority
              className="h-10 w-auto"
            />
          </a>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 lg:px-12 pb-12 relative z-10 w-full">
          <div className="w-full max-w-sm shrink-0">
            {children}
          </div>
        </div>
        <div className="lg:hidden pb-6 text-center text-xs text-gray-500 relative z-10 w-full">
          &copy; {new Date().getFullYear()} Finkr. All rights reserved.
        </div>
      </div>

    </div>
  );
}
